import { isEmpty } from "lodash";
import { ActionContext } from "vuex";

import Api from "@/api/Api";
import { ALL, SHOW_NULL_VALUES } from "@/constants";
import { CATALOGS_KEY, getCached, setCached } from "@/helpers/catalogsHelper";
import Lang from "@/lang";
import { API_ROUTES, requestTokens } from "@/settings/apiRoutes";
import { b2bb2c, campaignBudget } from "@/settings/dashboardFilters";
import store, { RootState } from "@/store";

export type Catalogs = {
  [key: string]: string[] | Record<string, string[]>;
};

export interface CatalogsState {
  catalogs: Catalogs | null;
  isLoading: boolean;
}

type ApiCampaignId = { name: string | null; id: string | null };
export type ApiResponse = {
  industries: string[];
  categories: string[];
  channels: Record<string, string>;
  campaign_kpis: string[];
  campaigns: string[];
  advertisers: string[];
  agencies: string[];
  business_goals: string[];
  media_types: string[];
  campaign_ids: ApiCampaignId[];
  publishers: string[];
  b2b_b2cs: string[];
  dsps: string[];
  dmas: string[];
  stores: string[];
  data_types: string[];
  subindustries: Record<string, string[]>;
};

type CatalogKey = keyof ApiResponse;
export const catalogsDictionary: Partial<Record<CatalogKey, string>> = {
  advertisers: "advertiser",
  agencies: "agency",
  business_goals: "businessGoal",
  campaigns: "campaign",
  campaign_kpis: "campaignKpi",
  campaign_ids: "campaignId",
  channels: "channel",
  industries: "industry",
  subindustries: "subindustry",
  media_types: "mediaType",
  dsps: "dsp",
  dmas: "dma",
  stores: "store",
  categories: "category",
  publishers: "publisher",
  b2b_b2cs: "b2b_b2c",
  data_types: "data_type",
};

const catalogsDefault: Catalogs = {
  advertiser: [],
  agency: [],
  businessGoal: [],
  campaign: [],
  channel: [],
  campaignKpi: [],
  campaignId: [],
  industry: [],
  subindustry: {},
  mediaType: [],
  category: [],
  publisher: [],
  b2b_b2c: [],
};

const catalogState: CatalogsState = {
  catalogs: null,
  isLoading: false,
};

export default {
  namespaced: true,

  state: catalogState,

  mutations: {
    updateCatalogs(state: CatalogsState, catalogs: Catalogs): void {
      state.catalogs = catalogs;
    },

    updateLoading(state: CatalogsState, val: boolean): void {
      state.isLoading = val;
    },

    resetCatalog(state: CatalogsState) {
      state.catalogs = null;
    },
  },

  actions: {
    async fetchCatalogs(ctx: ActionContext<CatalogsState, RootState>): Promise<boolean> {
      try {
        const cached = getCached(CATALOGS_KEY);
        if (cached) {
          ctx.commit("updateCatalogs", cached);
          return true;
        }

        ctx.commit("updateLoading", true);
        const res = await Api.requestWithRetry<ApiResponse>({
          path: API_ROUTES.CATALOG.PATH,
          defaultResult: {} as ApiResponse,
          token: requestTokens.apiCatalogs,
          errorCallback: async () => {
            ctx.commit("updateCatalogs", catalogsDefault);
          },
        });

        if (isEmpty(res)) {
          store.commit("app/showAlert", { message: Lang.errorGetCatalogs, type: "error" });
          ctx.commit("updateCatalogs", catalogsDefault);
          return false;
        }

        const filterData: Catalogs = {};

        for (const key of Object.keys(res) as CatalogKey[]) {
          const dictKey = catalogsDictionary[key];
          if (!dictKey) {
            continue;
          }

          if (key === "subindustries") {
            filterData[dictKey] = res.subindustries;
            continue;
          }

          if (key === "channels" && res.channels && typeof res.channels === "object" && !Array.isArray(res.channels)) {
            const names = Object.keys(res.channels).filter(Boolean).sort();
            filterData[dictKey] = [ALL, ...new Set(names), SHOW_NULL_VALUES];
            continue;
          }

          if (key === "campaign_ids" && Array.isArray(res.campaign_ids)) {
            const ids = Array.from(
              new Set(
                res.campaign_ids
                  .map((x) => x?.id)
                  .filter((x): x is string => typeof x === "string" && x.trim().length > 0)
              )
            ).sort();
            filterData[dictKey] = [ALL, ...ids, SHOW_NULL_VALUES];
            continue;
          }

          const arr = res[key];
          if (Array.isArray(arr)) {
            const strings = arr as string[];
            const cleaned = Array.from(
              new Set(strings.filter((x) => typeof x === "string" && x.trim().length > 0))
            ).sort();
            filterData[dictKey] = [ALL, ...cleaned, SHOW_NULL_VALUES];
            continue;
          }
        }

        if (!filterData["b2b_b2c"]) {
          filterData.b2b_b2c = b2bb2c.map((i) => i.text);
        }
        filterData.campaignBudget = campaignBudget.map((i) => i.text);

        ctx.commit("updateCatalogs", filterData);
        setCached(CATALOGS_KEY, filterData as unknown as Record<string, string[] | Record<string, string>[]>);

        return true;
      } catch (e) {
        console.error("[catalogs/fetchCatalogs] error", e);
        ctx.commit("updateCatalogs", catalogsDefault);
        return false;
      } finally {
        ctx.commit("updateLoading", false);
      }
    },
  },

  getters: {
    catalogs(state: CatalogsState): Catalogs | null {
      return state.catalogs;
    },

    isLoading(state: CatalogsState): boolean {
      return state.isLoading;
    },
  },
};
